import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Sun, Store, LogOut, Megaphone, GripVertical, FolderTree, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAdminTheme } from "@/admin/AdminTheme";
import { useAuth } from "@/context/AuthContext";
import { ACard, AButton, ATextarea, AInput, AdminPageTitle } from "@/admin/ui";
import { toast } from "sonner";

export default function AdminMore() {
  const { c, dark, toggle } = useAdminTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [editorials, setEditorials] = useState([]);
  const [site, setSite] = useState(null);
  const dragIndex = useRef(null);

  useEffect(() => { api("/admin/editorials").then(setEditorials).catch(() => {}); }, []);
  useEffect(() => { api("/settings", { auth: false }).then(setSite).catch(() => {}); }, []);

  const setContact = (k, v) => setSite((s) => ({ ...s, contact: { ...s.contact, [k]: v } }));
  const setSocial = (k, v) => setSite((s) => ({ ...s, social: { ...s.social, [k]: v } }));
  const setPolicy = (k, v) => setSite((s) => ({ ...s, policies: { ...s.policies, [k]: v } }));
  const saveSite = async () => {
    try {
      await api("/admin/settings", { method: "PUT", body: { about_text: site.about_text, contact: site.contact, social: site.social, policies: site.policies } });
      toast.success("Store content saved — live on web & mobile");
    } catch (e) { toast.error(e.message); }
  };

  const saveEditorial = async (ed) => {
    try { await api(`/admin/editorials/${ed.id}`, { method: "PUT", body: { title: ed.title, subtitle: ed.subtitle, tiles: ed.tiles, order: ed.order, active: ed.active } }); toast.success("Editorial saved"); }
    catch (e) { toast.error(e.message); }
  };
  const upd = (i, k, v) => setEditorials((eds) => eds.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  const updTile = (i, ti, k, v) => setEditorials((eds) => eds.map((e, j) => (j === i ? { ...e, tiles: e.tiles.map((t, k2) => (k2 === ti ? { ...t, [k]: v } : t)) } : e)));

  const onDrop = async (to) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === to) return;
    const next = [...editorials];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const reordered = next.map((e, i) => ({ ...e, order: i + 1 }));
    setEditorials(reordered);
    try {
      await Promise.all(reordered.map((e) => api(`/admin/editorials/${e.id}`, { method: "PUT", body: { title: e.title, subtitle: e.subtitle, tiles: e.tiles, order: e.order, active: e.active } })));
      toast.success("Order updated");
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="fade-up" style={{ maxWidth: 820 }}>
      <AdminPageTitle title="More" subtitle="Settings & storefront content" />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <AButton variant="soft" data-testid="more-catalog-link" onClick={() => navigate("/admin/catalog")}><FolderTree size={15} /><span style={{ marginLeft: 6 }}>Catalog manager</span></AButton>
        <AButton variant="soft" data-testid="more-stock-report-link" onClick={() => navigate("/admin/stock-report")}><AlertTriangle size={15} /><span style={{ marginLeft: 6 }}>Low-stock report</span></AButton>
      </div>

      {site && (
        <ACard style={{ marginBottom: 16 }}>
          <h3 className="font-display" style={{ margin: "0 0 14px", fontSize: 18, fontWeight: 700 }}>Store Content & Footer</h3>
          <div style={{ display: "grid", gap: 12 }}>
            <ATextarea label="About Us text" rows={4} data-testid="set-about" value={site.about_text || ""} onChange={(e) => setSite((s) => ({ ...s, about_text: e.target.value }))} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <AInput label="Contact Phone" data-testid="set-phone" value={site.contact?.phone || ""} onChange={(e) => setContact("phone", e.target.value)} />
              <AInput label="Contact Email" data-testid="set-email" value={site.contact?.email || ""} onChange={(e) => setContact("email", e.target.value)} />
            </div>
            <AInput label="Address" value={site.contact?.address || ""} onChange={(e) => setContact("address", e.target.value)} />
            <AInput label="Instagram URL" data-testid="set-instagram" value={site.social?.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} />
          </div>

          <h4 className="font-display" style={{ margin: "20px 0 10px", fontSize: 16, fontWeight: 700 }}>Consumer Policies</h4>
          <p style={{ fontSize: 12.5, color: c.mutedText, marginTop: 0 }}>Replace the [EDITABLE PLACEHOLDER] notes with your confirmed store policies before publishing.</p>
          <div style={{ display: "grid", gap: 12 }}>
            {(site.policy_keys || []).map((k) => (
              <ATextarea key={k} label={site.policy_titles?.[k] || k} rows={3} data-testid={`set-policy-${k}`} value={site.policies?.[k] || ""} onChange={(e) => setPolicy(k, e.target.value)} />
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            <AButton data-testid="save-site" onClick={saveSite}>Save Store Content</AButton>
          </div>
        </ACard>
      )}

      <ACard style={{ marginBottom: 16 }}>
        <h3 className="font-display" style={{ margin: "0 0 14px", fontSize: 18, fontWeight: 700 }}>Settings</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <AButton variant="outline" onClick={toggle}>{dark ? <Sun size={15} /> : <Moon size={15} />}<span style={{ marginLeft: 6 }}>{dark ? "Light" : "Dark"} mode</span></AButton>
          <AButton variant="outline" onClick={() => navigate("/")}><Store size={15} /><span style={{ marginLeft: 6 }}>View storefront</span></AButton>
          <AButton variant="danger" onClick={() => { logout(); navigate("/login"); }}><LogOut size={15} /><span style={{ marginLeft: 6 }}>Sign out</span></AButton>
        </div>
        <p style={{ fontSize: 13, color: c.mutedText, marginTop: 14, marginBottom: 0 }}>Signed in as <strong>{user?.name}</strong> ({user?.email})</p>
      </ACard>

      <ACard>
        <h3 className="font-display" style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}><Megaphone size={17} color={c.primary} /> Editorial Sections</h3>
        <p style={{ fontSize: 13, color: c.mutedText, marginTop: 0 }}>Edit the storefront home sections — changes appear on web & mobile. Drag <GripVertical size={12} style={{ verticalAlign: "middle" }} /> to reorder.</p>
        {editorials.map((ed, i) => (
          <div key={ed.id} data-testid={`editorial-row-${ed.id}`}
            draggable
            onDragStart={() => { dragIndex.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            style={{ borderTop: `1px solid ${c.divider}`, paddingTop: 14, marginTop: 14, display: "grid", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: c.mutedText }}>
              <GripVertical size={16} style={{ cursor: "grab" }} data-testid={`editorial-drag-${ed.id}`} />
              <span style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", fontWeight: 700 }}>Section {i + 1}</span>
            </div>
            <AInput label="Title" value={ed.title} onChange={(e) => upd(i, "title", e.target.value)} />
            <AInput label="Subtitle" value={ed.subtitle} onChange={(e) => upd(i, "subtitle", e.target.value)} />

            <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: c.mutedText, fontWeight: 700, marginTop: 4 }}>Tiles</div>
            {(ed.tiles || []).map((t, ti) => (
              <div key={ti} style={{ display: "flex", gap: 10, alignItems: "center", background: c.surfaceAlt, borderRadius: 10, padding: 10 }}>
                <div style={{ width: 52, height: 52, borderRadius: 8, overflow: "hidden", background: c.bg, flexShrink: 0, border: `1px solid ${c.border}` }}>
                  {t.image && <img src={t.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
                <div style={{ flex: 1, display: "grid", gap: 6 }}>
                  <AInput data-testid={`editorial-tile-label-${ed.id}-${ti}`} value={t.label} onChange={(e) => updTile(i, ti, "label", e.target.value)} placeholder="Tile label" style={{ padding: "8px 10px" }} />
                  <AInput data-testid={`editorial-tile-image-${ed.id}-${ti}`} value={t.image} onChange={(e) => updTile(i, ti, "image", e.target.value)} placeholder="Image URL" style={{ padding: "8px 10px" }} />
                </div>
              </div>
            ))}

            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={ed.active} onChange={(e) => upd(i, "active", e.target.checked)} style={{ width: 16, height: 16 }} /> Active
              </label>
              <AButton variant="soft" data-testid={`editorial-save-${ed.id}`} onClick={() => saveEditorial(ed)}>Save</AButton>
            </div>
          </div>
        ))}
      </ACard>
    </div>
  );
}
